import type { Dispatch, SetStateAction } from 'react'
import { doc, writeBatch } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import { writeChecklistUpdate } from '../../services/checklistWriter'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import type { MarkdownSyncMetadata } from '../../context/TimelineContext'
import { parseMarkdownTaskPayload } from '../../utils/markdownSync'
import { truncateToLimit } from '../../utils/checklistLimits'
import { nodesToElementChecklist } from './liveReconciliation'

export interface UseBoardMarkdownSyncParams {
  canEditStructure: boolean
  checklistLimits: { commentMaxLength: number }
  currentDisplayName: string
  currentUser: { uid?: string; email?: string | null } | null
  hasElementMirror: boolean
  item: { id: string }
  nodes: BoardChecklistItem[]
  setMarkdownMetadata: Dispatch<SetStateAction<MarkdownSyncMetadata>>
  setNodes: Dispatch<SetStateAction<BoardChecklistItem[]>>
  showFlash: (message: string) => void
}

export function useBoardMarkdownSync({
  canEditStructure,
  checklistLimits,
  currentDisplayName,
  currentUser,
  hasElementMirror,
  item,
  nodes,
  setMarkdownMetadata,
  setNodes,
  showFlash,
}: UseBoardMarkdownSyncParams) {
  const linkMarkdownRepository = (repoPath: string) => {
    const nextCommit = Math.random().toString(16).slice(2, 9)
    setMarkdownMetadata({
      repoPath: repoPath.trim() || 'github://workspace/docs/PLAN.md',
      lastSyncedCommit: nextCommit,
      isSyncingInProgress: false,
    })
    showFlash('Linked markdown source.')
  }

  const applyMarkdownInboundSync = async (markdownPayload: string) => {
    if (!canEditStructure) return
    const parsedTasks = parseMarkdownTaskPayload(markdownPayload)
    if (parsedTasks.length === 0) return

    setMarkdownMetadata((current) => ({ ...current, isSyncingInProgress: true }))
    const batch = writeBatch(firestoreDb)
    const markdownActor = { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null }
    const nativeWrites: Promise<unknown>[] = []
    const nextNodes = nodes.map((node) => {
      const match = parsedTasks.find((task) => task.title.toLowerCase() === node.title.toLowerCase())
      if (!match) return node
      const checklistUpdates = {
        title: match.title,
        isCompleted: match.isCompleted,
        ...(match.description ? { ingestionDescription: truncateToLimit(match.description, checklistLimits.commentMaxLength) } : {}),
      }
      nativeWrites.push(
        writeChecklistUpdate(node.id, checklistUpdates, markdownActor)
          .catch((err) => console.debug('[ChecklistDrawingBoard] native markdown sync skipped', (err as Error)?.message)),
      )
      batch.update(doc(firestoreDb, 'checklists', node.id), checklistUpdates)
      return {
        ...node,
        title: match.title,
        isCompleted: match.isCompleted,
      }
    })
    void Promise.all(nativeWrites)

    if (hasElementMirror) {
      batch.update(doc(firestoreDb, 'elements', item.id), {
        checklist: nodesToElementChecklist(item.id, nextNodes),
      })
    }
    await batch.commit()
    setNodes(nextNodes)
    setMarkdownMetadata((current) => ({
      ...current,
      lastSyncedCommit: Math.random().toString(16).slice(2, 9),
      isSyncingInProgress: false,
    }))
    showFlash('Markdown webhook synchronized.')
  }

  return {
    linkMarkdownRepository,
    applyMarkdownInboundSync,
  }
}
